import { useEffect, useState, useRef, type ReactNode } from "react";
import { Outlet, Link, useLocation } from "react-router-dom";
import { LayoutDashboard, LogOut, Shield, KeyRound, MapPin } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { ToastProvider } from "../contexts/ToastContext";
import { setAuthTokenProvider, changePassword } from "../api/client";
import { friendlyError } from "../utils/errors";
import NotificationBell from "./NotificationBell";

const authDisabled = process.env.REACT_APP_AUTH_DISABLED === "true";
const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:8000";

export default function Layout({ children }: { children?: ReactNode } = {}) {
  const location = useLocation();
  const isHome = location.pathname === "/";
  const isComps = location.pathname === "/comps";
  const isAdmin = location.pathname === "/admin";

  // Network connectivity check
  const [serverUnreachable, setServerUnreachable] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const resp = await fetch(`${API_BASE}/api/health`, { method: "GET" });
        if (resp.ok) {
          setServerUnreachable(false);
        } else {
          setServerUnreachable(true);
        }
      } catch {
        setServerUnreachable(true);
      }
    };

    checkHealth();

    intervalRef.current = setInterval(checkHealth, 30000);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return (
    <ToastProvider>
      <div className="min-h-screen flex flex-col bg-grove-50">
        {serverUnreachable && (
          <div className="bg-red-600 text-white text-sm text-center px-4 py-2 print:hidden">
            Unable to reach the server. Check your connection.
          </div>
        )}
        <header className="bg-white border-b border-grove-300 sticky top-0 z-30 print:hidden shadow-sm">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-8 h-14 sm:h-16 flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 group">
              <div className="w-9 h-9 rounded-lg bg-forest-700 flex items-center justify-center shadow-md">
                <span className="text-white font-bold text-sm tracking-tight">AV</span>
              </div>
              <span className="font-display text-xl font-semibold tracking-tight text-bark-800">
                AutoVal
              </span>
            </Link>
            <nav className="flex items-center gap-2">
              <Link
                to="/"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isHome
                    ? "bg-forest-100 text-forest-700"
                    : "text-bark-500 hover:text-bark-800 hover:bg-grove-100"
                }`}
              >
                <span className="flex items-center gap-2">
                  <LayoutDashboard className="w-4 h-4" />
                  Deals
                </span>
              </Link>
              <Link
                to="/comps"
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                  isComps
                    ? "bg-forest-100 text-forest-700"
                    : "text-bark-500 hover:text-bark-800 hover:bg-grove-100"
                }`}
              >
                <span className="flex items-center gap-2">
                  <MapPin className="w-4 h-4" />
                  Comps
                </span>
              </Link>
              {!authDisabled && <NotificationBell />}
              {!authDisabled && <UserMenu isAdmin={isAdmin} />}
            </nav>
          </div>
        </header>
        <main className="flex-1">
          {children ?? <Outlet />}
        </main>
      </div>
    </ToastProvider>
  );
}

function UserMenu({ isAdmin: isAdminPage }: { isAdmin: boolean }) {
  const { user, token, logout } = useAuth();
  const [showPwModal, setShowPwModal] = useState(false);

  useEffect(() => {
    setAuthTokenProvider(() => Promise.resolve(token));
  }, [token]);

  if (!user) return null;

  return (
    <>
      <div className="flex items-center gap-2 ml-4 pl-4 border-l border-grove-300">
        {user.is_admin && (
          <Link
            to="/admin"
            className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-all duration-200 ${
              isAdminPage
                ? "bg-forest-100 text-forest-700 font-medium"
                : "text-bark-400 hover:text-bark-700 hover:bg-grove-100"
            }`}
          >
            <Shield className="w-4 h-4" />
            <span className="hidden sm:inline">Admin</span>
          </Link>
        )}
        <span className="text-sm text-bark-400 hidden sm:inline">
          {user.email}
        </span>
        <button
          onClick={() => setShowPwModal(true)}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-bark-400 hover:text-bark-700 hover:bg-grove-100 rounded-lg transition-all duration-200"
          title="Change password"
        >
          <KeyRound className="w-4 h-4" />
        </button>
        <button
          onClick={logout}
          className="flex items-center gap-1.5 px-3 py-2 text-sm text-bark-400 hover:text-bark-700 hover:bg-grove-100 rounded-lg transition-all duration-200"
        >
          <LogOut className="w-4 h-4" />
          <span className="hidden sm:inline">Sign out</span>
        </button>
      </div>
      {showPwModal && (
        <ChangePasswordModal onClose={() => setShowPwModal(false)} />
      )}
    </>
  );
}

function ChangePasswordModal({ onClose }: { onClose: () => void }) {
  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (newPw.length < 8) {
      setError("New password must be at least 8 characters");
      return;
    }
    if (newPw !== confirm) {
      setError("Passwords do not match");
      return;
    }

    setLoading(true);
    try {
      await changePassword(current, newPw);
      setSuccess(true);
      setTimeout(onClose, 1500);
    } catch (err: any) {
      setError(
        friendlyError(err?.response?.data?.detail || "Couldn't change your password. Check your current password and try again.")
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm mx-4">
        <h3 className="font-display text-lg font-semibold text-bark-800 mb-4">
          Change Password
        </h3>
        {success ? (
          <p className="text-forest-700 text-sm">Password changed successfully.</p>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            {error && (
              <p className="text-red-600 text-sm bg-red-50 px-3 py-2 rounded-lg">
                {error}
              </p>
            )}
            <input
              type="password"
              placeholder="Current password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full px-3 py-2 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              required
            />
            <input
              type="password"
              placeholder="New password (min 8 characters)"
              value={newPw}
              onChange={(e) => setNewPw(e.target.value)}
              className="w-full px-3 py-2 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              required
            />
            <input
              type="password"
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full px-3 py-2 border border-grove-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-forest-500"
              required
            />
            <div className="flex gap-2 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-forest-700 text-white text-sm font-medium rounded-lg hover:bg-forest-800 disabled:opacity-50 transition-colors"
              >
                {loading ? "Changing..." : "Change Password"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm text-bark-500 hover:text-bark-700 border border-grove-300 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
